import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ColumnSizingState, OnChangeFn } from '@tanstack/react-table';

/**
 * 주문 테이블의 컬럼 순서/너비를 탭별로 localStorage 에 영속화한다.
 * - 저장된 순서는 현재 컬럼 구성에 맞춰 정합(reconcile)한다(없는 컬럼 제거, 새 컬럼 추가, 고정 컬럼 맨앞).
 * - 변경은 debounce 후 저장한다.
 */

const STORAGE_PREFIX = 'sellerbox:order-table:';
const STORAGE_VERSION = 1;
const SAVE_DEBOUNCE_MS = 200;

type StoredPreference = {
  version: number;
  columnOrder: string[];
  columnSizing: Record<string, number>;
};

const readPreference = (key: string): StoredPreference | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredPreference;
    if (parsed.version !== STORAGE_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writePreference = (key: string, value: StoredPreference) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
  } catch {
    // 용량 초과/프라이빗 모드 등은 무시(영속화 실패해도 동작은 유지).
  }
};

/** 저장된 순서를 현재 컬럼에 맞게 정합한다: 고정 첫 컬럼 맨앞, 현재 존재하는 id만, 새 id는 뒤에. */
const reconcileOrder = (saved: string[], current: string[], pinnedFirstId?: string): string[] => {
  const currentSet = new Set(current);
  const kept = saved.filter((id) => currentSet.has(id));
  const keptSet = new Set(kept);
  const appended = current.filter((id) => !keptSet.has(id));
  let merged = [...kept, ...appended];
  if (pinnedFirstId && merged.includes(pinnedFirstId)) {
    merged = [pinnedFirstId, ...merged.filter((id) => id !== pinnedFirstId)];
  }
  return merged;
};

type UseOrderTablePreferenceParams = {
  /** 탭별 고유 키(예: 'new-order'). STORAGE_PREFIX 와 합쳐 저장된다. */
  storageKey: string;
  /** 현재 컬럼 id 들(기본 순서). */
  columnIds: string[];
  /** 항상 맨앞에 고정할 컬럼 id(체크박스). */
  pinnedFirstId?: string;
};

export const useOrderTablePreference = ({
  storageKey,
  columnIds,
  pinnedFirstId = 'checkbox',
}: UseOrderTablePreferenceParams) => {
  const [columnOrder, setColumnOrderState] = useState<string[]>(() =>
    reconcileOrder(readPreference(storageKey)?.columnOrder ?? [], columnIds, pinnedFirstId),
  );
  const [columnSizing, setColumnSizingState] = useState<ColumnSizingState>(
    () => readPreference(storageKey)?.columnSizing ?? {},
  );

  // 컬럼 구성이 바뀌면(예: 체크박스/주문번호 컬럼 추가·제거) 순서를 현재에 맞게 정합한다.
  const columnIdsKey = useMemo(() => columnIds.join('|'), [columnIds]);
  useEffect(() => {
    setColumnOrderState((prev) => reconcileOrder(prev, columnIdsKey.split('|'), pinnedFirstId));
  }, [columnIdsKey, pinnedFirstId]);

  // 변경 사항을 debounce 후 저장한다(외부 시스템(localStorage)과의 동기화).
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (typeof window === 'undefined') return;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      writePreference(storageKey, { version: STORAGE_VERSION, columnOrder, columnSizing });
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(saveTimerRef.current);
  }, [storageKey, columnOrder, columnSizing]);

  const setColumnOrder = useCallback<OnChangeFn<string[]>>((updater) => {
    setColumnOrderState((prev) => (typeof updater === 'function' ? updater(prev) : updater));
  }, []);

  const setColumnSizing = useCallback<OnChangeFn<ColumnSizingState>>((updater) => {
    setColumnSizingState((prev) => (typeof updater === 'function' ? updater(prev) : updater));
  }, []);

  const resetPreference = useCallback(() => {
    setColumnOrderState(reconcileOrder([], columnIdsKey.split('|'), pinnedFirstId));
    setColumnSizingState({});
  }, [columnIdsKey, pinnedFirstId]);

  return { columnOrder, setColumnOrder, columnSizing, setColumnSizing, resetPreference };
};
